import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import SportsIcon from '@mui/icons-material/Sports';
import GroupsIcon from '@mui/icons-material/Groups';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { MAIN_APP_DASHBOARD_URL } from '@/types';
import { GLASS_BLUR, glassBackground, glassBorder } from '@/utils/glassTokens';

export const DRAWER_WIDTH = 260;

const navItems = [
  { to: '/dashboard', label: '數據總覽', icon: DashboardIcon },
  { to: '/review', label: '影片分析', icon: PlayCircleIcon },
  { to: '/groups', label: '裁判群組', icon: GroupsIcon },
  { to: '/live', label: '即時記錄', icon: SportsIcon },
  { to: '/scoreboard', label: '賽事比分牌', icon: ScoreboardIcon },
] as const;

type SideMenuProps = {
  open: boolean;
  onClose: () => void;
  variant: 'temporary' | 'persistent';
};

export default function SideMenu({ open, onClose, variant }: SideMenuProps) {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  const isActive = (path: string) =>
    path === '/dashboard' ? location.pathname === '/dashboard' : location.pathname.startsWith(path);

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isDark ? 'rgba(8, 12, 18, 0.92)' : 'rgba(255, 255, 255, 0.92)',
        backdropFilter: GLASS_BLUR,
        WebkitBackdropFilter: GLASS_BLUR,
        color: isDark ? 'rgba(255,255,255,0.92)' : 'text.primary',
      }}
    >
      {isMobile && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              minHeight: 56,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
              分析工作台
            </Typography>
            <IconButton onClick={onClose} aria-label="關閉選單" size="small">
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider' }} />
        </>
      )}

      <List sx={{ flex: 1, py: 1.5, px: 0.5, overflowY: 'auto' }}>
        {!isMobile && (
          <Typography
            variant="subtitle2"
            sx={{ px: 2, py: 1, color: 'text.secondary', display: 'block' }}
          >
            分析工作台
          </Typography>
        )}
        {navItems.map(({ to, label, icon: Icon }) => (
          <ListItemButton
            key={to}
            component={RouterLink}
            to={to}
            selected={isActive(to)}
            onClick={() => isMobile && onClose()}
            sx={{
              '&.Mui-selected': {
                bgcolor: isDark ? 'rgba(139, 195, 74, 0.14)' : 'rgba(139, 195, 74, 0.12)',
                color: 'secondary.main',
                '& .MuiListItemIcon-root': { color: 'secondary.main' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={label} primaryTypographyProps={{ fontSize: '0.9375rem' }} />
          </ListItemButton>
        ))}
      </List>

      <Divider sx={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider' }} />

      <List sx={{ py: 1.5, px: 0.5 }}>
        <ListItemButton
          component="a"
          href={MAIN_APP_DASHBOARD_URL}
          sx={{
            color: isDark ? 'rgba(255,255,255,0.78)' : 'text.secondary',
            '&:hover': {
              bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'action.hover',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <ArrowBackIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="返回主系統"
            secondary="www.hkhbref.com"
            primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: 600 }}
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </ListItemButton>
      </List>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: glassBorder(isDark),
          bgcolor: glassBackground(isDark),
        }}
      >
        <Typography variant="caption" color="text.secondary">
          賽事數據分析模組
        </Typography>
      </Box>
    </Box>
  );

  if (variant === 'persistent') {
    return (
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            border: 'none',
            top: 64,
            height: 'calc(100% - 64px)',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: 'block', md: 'none' },
        '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
